/**
 * MatrixSidebar Component
 * Gap Analysis sidebar with compliance stats and filter buttons
 *
 * NOTE: This sidebar is currently DISABLED in MatrixPage.jsx
 * To enable, remove the `{false && (` wrapper in MatrixPage.jsx
 */

import { useState, memo, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart3,
  ChevronRight,
  CheckCircle,
  Clock,
  AlertTriangle,
  XCircle,
  FileText,
  Calendar,
  CheckSquare,
  Square,
  Info
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/components/ui/accordion';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';

// Status filter button sub-component
const StatusFilterButton = memo(({
  filter,
  label,
  icon: Icon,
  color,
  count,
  activeFilters,
  toggleFilter,
  expanded = true
}) => {
  const isActive = activeFilters.has(filter);

  const colorClasses = {
    green: {
      active: 'bg-green-500 text-white',
      inactive: 'bg-white border border-gray-200 hover:border-green-300',
      badge: 'bg-green-600'
    },
    yellow: {
      active: 'bg-yellow-500 text-white',
      inactive: 'bg-white border border-gray-200 hover:border-yellow-300',
      badge: 'bg-yellow-600'
    },
    red: {
      active: 'bg-red-500 text-white',
      inactive: 'bg-white border border-gray-200 hover:border-red-300',
      badge: 'bg-red-600'
    },
    purple: {
      active: 'bg-purple-500 text-white',
      inactive: 'bg-white border border-gray-200 hover:border-purple-300',
      badge: 'bg-purple-600'
    },
    blue: {
      active: 'bg-blue-500 text-white',
      inactive: 'bg-white border border-gray-200 hover:border-blue-300',
      badge: 'bg-blue-600'
    },
    indigo: {
      active: 'bg-indigo-500 text-white',
      inactive: 'bg-white border border-gray-200 hover:border-indigo-300',
      badge: 'bg-indigo-600'
    },
    gray: {
      active: 'bg-gray-500 text-white',
      inactive: 'bg-white border border-gray-200 hover:border-gray-300',
      badge: 'bg-gray-600'
    }
  };

  const classes = colorClasses[color];

  if (!expanded) {
    // Collapsed view
    return (
      <button
        onClick={() => toggleFilter(filter)}
        className={`relative group w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
          isActive ? classes.active : classes.inactive
        }`}
        title={`${label}: ${count || 0}`}
      >
        <Icon className="h-5 w-5" />
        {count > 0 && (
          <span className={`absolute -top-1 -right-1 ${classes.badge} text-white text-[9px] rounded-full h-4 flex items-center justify-center font-bold ${
            count > 99 ? 'px-1 min-w-[20px]' : 'w-4'
          }`}>
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>
    );
  }

  // Expanded view
  return (
    <button
      onClick={() => toggleFilter(filter)}
      className={`relative group flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
        isActive ? classes.active : classes.inactive
      }`}
      title={label}
    >
      <Icon className="h-5 w-5 mb-1" />
      {count > 0 && (
        <span className={`absolute -top-1 -right-1 ${classes.badge} text-white text-[9px] rounded-full h-4 flex items-center justify-center font-bold ${
          count > 99 ? 'px-1 min-w-[20px]' : 'w-4'
        }`}>
          {count > 99 ? '99+' : count}
        </span>
      )}
      <span className="text-[10px] font-medium mt-0.5">{label}</span>
    </button>
  );
});

StatusFilterButton.displayName = 'StatusFilterButton';

const MatrixSidebar = memo(({
  // Stats
  sidebarStats,
  overallComplianceRate = 0,
  projectedComplianceRate = 0,

  // Filter state
  activeFilters,
  toggleFilter,
  clearFilters,

  // Employee stats (for expanded accordion)
  employeeStatsMap,
  teamMembers,

  // UI state
  expanded: initialExpanded = false
}) => {
  const [expanded, setExpanded] = useState(initialExpanded);

  // Filter buttons config
  const statusFilters = [
    { filter: 'compliant', label: 'Valid', icon: CheckCircle, color: 'green', count: sidebarStats.compliant },
    { filter: 'expiring', label: 'Expiring', icon: Clock, color: 'yellow', count: sidebarStats.expiring },
    { filter: 'expired', label: 'Expired', icon: AlertTriangle, color: 'red', count: sidebarStats.expired },
    { filter: 'missing', label: 'Missing', icon: XCircle, color: 'red', count: sidebarStats.missing },
    { filter: 'quotes', label: 'Quotes', icon: FileText, color: 'purple', count: sidebarStats.quotes },
    { filter: 'booked', label: 'Booked', icon: Calendar, color: 'blue', count: sidebarStats.booked }
  ];

  const assignmentFilters = [
    { filter: 'assigned', label: 'Assigned', icon: CheckSquare, color: 'indigo' },
    { filter: 'unassigned', label: 'Unassigned', icon: Square, color: 'gray' }
  ];

  return (
    <div
      className={`bg-gray-50 border-l border-gray-200 flex flex-col flex-shrink-0 ${
        expanded ? 'w-72' : 'w-16'
      }`}
      style={{ transition: 'width 0.5s ease-in-out' }}
    >
      {/* Sidebar Header */}
      <div
        className={`border-b border-gray-200 bg-white flex items-center flex-shrink-0 ${
          expanded ? 'justify-between' : 'justify-center'
        }`}
        style={{
          height: '64px',
          minHeight: '64px',
          maxHeight: '64px',
          paddingLeft: expanded ? '12px' : '4px',
          paddingRight: expanded ? '12px' : '4px'
        }}
      >
        <button
          onClick={() => setExpanded(!expanded)}
          className={`flex items-center hover:bg-blue-50 rounded-lg ${
            !expanded ? 'flex-col gap-0.5 p-1.5' : 'gap-1 p-2'
          }`}
          title={expanded ? "Collapse Gap Analysis" : "Expand Gap Analysis"}
        >
          <BarChart3
            className={`text-blue-600 flex-shrink-0 ${
              expanded ? 'h-5 w-5' : 'h-7 w-7'
            }`}
          />
          <ChevronRight
            className={`text-blue-600 flex-shrink-0 ${
              expanded ? 'h-4 w-4' : 'h-5 w-5'
            }`}
            style={{
              transform: expanded ? 'rotate(0deg)' : 'rotate(180deg)',
              transition: 'transform 0.5s ease-in-out'
            }}
          />
        </button>

        {expanded && (
          <div className="flex items-center justify-between flex-1">
            <h3 className="text-sm font-semibold text-gray-900 flex-1 text-center whitespace-nowrap">
              Gap Analysis
            </h3>
            <button
              onClick={clearFilters}
              className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100 whitespace-nowrap"
              title="Clear all filters"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Sidebar Content */}
      <div
        className="flex-1 overflow-y-auto"
        onWheel={(e) => e.stopPropagation()}
      >
        <AnimatePresence mode="wait">
          {expanded ? (
            <motion.div
              key="expanded"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="p-3"
            >
              {/* Quick Stats Summary */}
              <div className="mb-4 p-3 bg-white rounded-lg border border-gray-200">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="text-gray-600">Total Team:</div>
                  <div className="font-semibold text-right">{sidebarStats.totalEmployees || 0}</div>
                  <div className="text-gray-600">Requirements:</div>
                  <div className="font-semibold text-right">{sidebarStats.totalRequirements || 0}</div>
                  <div className="text-gray-600 col-span-2 pt-1 mt-1 border-t border-gray-200" />
                  <div className="text-gray-600">Compliance Rate:</div>
                  <div className={`font-semibold text-right ${
                    overallComplianceRate >= 80 ? 'text-green-600' :
                    overallComplianceRate >= 50 ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {overallComplianceRate}%
                  </div>
                  <div className="text-gray-600 flex items-center gap-1">
                    Projected:
                    <TooltipProvider>
                      <Tooltip delayDuration={200}>
                        <TooltipTrigger asChild>
                          <Info className="h-3 w-3 text-gray-400 cursor-pointer hover:text-gray-600 transition-colors" />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="text-xs">
                          <p>Includes booked training</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <div className={`font-semibold text-right ${
                    projectedComplianceRate >= 80 ? 'text-blue-600' :
                    projectedComplianceRate >= 50 ? 'text-yellow-600' :
                    'text-orange-600'
                  }`}>
                    {projectedComplianceRate}%
                  </div>
                </div>
              </div>

              {/* Status Filter Grid */}
              <div className="grid grid-cols-3 gap-2">
                {statusFilters.map(filterConfig => (
                  <StatusFilterButton
                    key={filterConfig.filter}
                    {...filterConfig}
                    activeFilters={activeFilters}
                    toggleFilter={toggleFilter}
                    expanded={true}
                  />
                ))}
              </div>

              {/* Divider */}
              <div className="my-3 border-t border-gray-200" />

              {/* Assignment Status Section */}
              <div className="mb-2">
                <h4 className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide px-1 mb-2">
                  Assignment Status
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {assignmentFilters.map(filterConfig => (
                    <StatusFilterButton
                      key={filterConfig.filter}
                      {...filterConfig}
                      count={0}
                      activeFilters={activeFilters}
                      toggleFilter={toggleFilter}
                      expanded={true}
                    />
                  ))}
                </div>
              </div>

              {/* Employee Accordion (optional) */}
              {employeeStatsMap && teamMembers && (
                <Accordion type="single" collapsible className="mt-4">
                  <AccordionItem value="employees" className="border-0">
                    <AccordionTrigger className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide px-1 py-2 hover:no-underline">
                      By Employee ({teamMembers.length})
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-1 max-h-[200px] overflow-y-auto">
                        {teamMembers
                          .filter(m => {
                            const stats = employeeStatsMap.get(m.id);
                            return stats && stats.totalAssigned > 0;
                          })
                          .map(member => {
                            const stats = employeeStatsMap.get(member.id);
                            return (
                              <div
                                key={member.id}
                                className="flex items-center justify-between text-xs py-1 px-2 hover:bg-gray-100 rounded"
                              >
                                <span className="truncate flex-1">{member.name}</span>
                                <span className={`font-medium ${
                                  stats.compliancePercentage >= 80 ? 'text-green-600' :
                                  stats.compliancePercentage >= 50 ? 'text-yellow-600' :
                                  'text-red-600'
                                }`}>
                                  {stats.compliancePercentage}%
                                </span>
                              </div>
                            );
                          })}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              )}
            </motion.div>
          ) : (
            /* Collapsed view - compact icons */
            <motion.div
              key="collapsed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="flex flex-col items-center space-y-2 py-2"
            >
              {statusFilters.slice(0, 4).map(filterConfig => (
                <StatusFilterButton
                  key={filterConfig.filter}
                  {...filterConfig}
                  activeFilters={activeFilters}
                  toggleFilter={toggleFilter}
                  expanded={false}
                />
              ))}

              <div className="w-8 border-t border-gray-300" />

              {statusFilters.slice(4).map(filterConfig => (
                <StatusFilterButton
                  key={filterConfig.filter}
                  {...filterConfig}
                  activeFilters={activeFilters}
                  toggleFilter={toggleFilter}
                  expanded={false}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
});

MatrixSidebar.displayName = 'MatrixSidebar';

export default MatrixSidebar;
